import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import type { specialIndexContentItemContent$key } from "scmp-app/queries/__generated__/specialIndexContentItemContent.graphql";

import { WithCustomQueryVariant } from "./consts";
import { SpecialIndexTag } from "./special-index-tag";
import {
  Container,
  ContentContainer,
  Cover,
  Footer,
  Headline,
  StyledCoverEntityLink,
  StyledHeadlineEntityLink,
  TagAndHeadlineContainer,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  imageAspectRatio?: string;
  isCommentsVisible?: boolean;
  reference: specialIndexContentItemContent$key;
  shouldShowContentTypeIcon?: boolean;
  shouldShowDefaultImage?: boolean;
  variant: SpecialIndexVariant;
};

export type SpecialIndexVariant = "others" | "series" | "spotlight" | "talking-post";

export const ContentItemSpecialIndex: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  imageAspectRatio,
  isCommentsVisible,
  ref,
  reference,
  shouldShowContentTypeIcon,
  shouldShowDefaultImage,
  topicLinkVariant,
  variant,
}) => {
  const entity = useFragment(
    graphql`
      fragment specialIndexContentItemContent on Content {
        __typename
        headline
        socialHeadline
        ...specialIndexTagContent
        ...entityLink
        ...helpersUseContentSponsorContent
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withCoverImage: true
            withCoverImageSize1280x720: true
            withCoverImageSize640x360: true
            withActionBar: true
            withSponsorTag: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(entity, topicLinkVariant);

  const headline = entity.socialHeadline ?? entity.headline;

  const cover = (
    <StyledCoverEntityLink reference={entity}>
      {providers.coverImage({
        responsiveVariants: {
          desktopUp: "size1280x720",
          mobileUp: "size640x360",
          tabletUp: "size1280x720",
        },
        shouldShowDefaultImage,
      })}
    </StyledCoverEntityLink>
  );

  const contentSponsor = getContentSponsor(entity);
  const isSponsorArticle = notEmpty(contentSponsor?.name);
  const footer = isSponsorArticle
    ? providers.sponsorTag?.({
        color: "#001246",
        gap: "0px",
        layoutDirection: "vertical",
        nameFontSize: "12px",
        typeFontSize: "12px",
      })
    : providers.actionBar({
        isCommentsVisible,
        shouldShowContentTypeIcon,
      });

  const isWithCustomQueryRoute = WithCustomQueryVariant.includes(variant);
  const customQueryParameters = isWithCustomQueryRoute
    ? {
        campaign: headline?.trim() ?? "",
        module: tracking.module.SeriesSection,
        pgtype: tracking.pageType.Series,
      }
    : undefined;

  return (
    <BaseLinkContextProvider customQueryParameters={customQueryParameters}>
      <Container className={className} ref={ref}>
        <Cover $imageAspectRatio={imageAspectRatio}>{cover}</Cover>
        <ContentContainer>
          <TagAndHeadlineContainer>
            <SpecialIndexTag reference={entity} variant={variant} />
            <StyledHeadlineEntityLink reference={entity}>
              <Headline>{headline}</Headline>
            </StyledHeadlineEntityLink>
          </TagAndHeadlineContainer>
          <Footer>{footer}</Footer>
        </ContentContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

ContentItemSpecialIndex.displayName = "ContentItemSpecialIndex";
